"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { consumeRailScroll, registerScrollRail } from "@/lib/navigation-scroll";

function isOnScreen(node: HTMLElement) {
  // Next keeps the route you navigated away from mounted in a hidden subtree,
  // so a rail can be alive and stale at the same time. offsetParent is null for
  // anything inside `display: none`.
  return node.isConnected && node.offsetParent !== null;
}

/**
 * Keeps a horizontal rail where the reader left it across a navigation away and
 * back. Returns a ref callback for the scrolling element.
 *
 * There are two different losses to cover, and they need different mechanisms:
 *
 * 1. **The route is kept but hidden.** With `cacheComponents`, Next hides the
 *    route you navigate away from using React's `<Activity>`, which sets
 *    `display: none`. That subtree has no layout boxes, so the browser discards
 *    the scroll offset of every scroll container inside it — the element itself
 *    survives, its scrollLeft does not. Next's own scroll restoration covers the
 *    document scroll, not nested scrollers. So the position is carried across in
 *    a ref: a layout-effect cleanup runs synchronously *before* Activity hides
 *    the subtree (while the box still exists), and the effect body re-runs when
 *    it is revealed.
 *
 * 2. **The route is dropped.** Next keeps at most 3 route entries; past that the
 *    oldest is evicted and re-renders from scratch, as does any full document
 *    load. Nothing in memory survives that, so the position comes back from the
 *    sessionStorage record staged by the back navigation (lib/navigation-scroll).
 *
 * Give every rail on a page a distinct key. Keys do not have to be unique across
 * routes, because a staged position is only handed back on the href it was
 * captured on.
 */
export function useRailScrollRestore<T extends HTMLElement>(
  restoreKey?: string,
) {
  const [node, setNode] = useState<T | null>(null);
  const railRef = useCallback((next: T | null) => {
    setNode(next);
  }, []);

  // Set by the cleanup below, read by the next run of the effect. Non-null only
  // between an Activity hide and the matching reveal.
  const carriedRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!restoreKey || !node) return;

    return registerScrollRail({
      key: restoreKey,
      read: () => (isOnScreen(node) ? Math.round(node.scrollLeft) : null),
    });
  }, [node, restoreKey]);

  useLayoutEffect(() => {
    if (!node) return;

    const carried = carriedRef.current;
    carriedRef.current = null;

    // A rail revealed from `display: none` carries its own position. Only a rail
    // with nothing to carry — a fresh mount — falls back to the staged record,
    // and only when it is the one actually on screen: reading it while hidden
    // would consume it on behalf of the rail the reader is really looking at.
    const saved =
      carried ??
      (restoreKey && isOnScreen(node) ? consumeRailScroll(restoreKey) : null);

    if (saved !== null && saved > 0) {
      const apply = () => {
        const previousBehavior = node.style.scrollBehavior;
        const previousSnap = node.style.scrollSnapType;
        node.style.scrollBehavior = "auto";
        node.style.scrollSnapType = "none";
        node.scrollLeft = saved;
        node.style.scrollBehavior = previousBehavior;
        node.style.scrollSnapType = previousSnap;
      };

      apply();

      // Cards size themselves from percentage bases and streamed images, so the
      // rail can still be narrower than its final width on this frame. Reapply
      // once layout has settled.
      const frame = requestAnimationFrame(apply);

      return () => {
        cancelAnimationFrame(frame);
        carriedRef.current = node.scrollLeft || null;
      };
    }

    return () => {
      // Runs before Activity hides the subtree, so the box — and its scroll
      // offset — is still there to read.
      carriedRef.current = node.scrollLeft || null;
    };
  }, [node, restoreKey]);

  return railRef;
}
